import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, MessageSquare } from "lucide-react";

import { useCommentOnEntryMutation } from "../api/logbookApi";

/**
 * The supervisor's review of one day.
 *
 * Separate from approve and reject, because most review is neither — "this
 * week is thin on measurement, put the site visits under TCD/B2" is guidance
 * on an entry that is otherwise fine, and making a supervisor reject the day
 * in order to say it would misrepresent the assessment record.
 *
 * An unanswered review shades its week red on the calendar, so the state this
 * writes is visible from the week grid without opening every day.
 */
export default function EntryReviewBox({
  entry,
  disabled,
  onSaved,
}: {
  entry: any;
  disabled?: boolean;
  onSaved?: () => void | Promise<any>;
}) {
  const existing = String(entry?.supervisor_comment ?? "");
  const [draft, setDraft] = useState(existing);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [saveComment, { isLoading }] = useCommentOnEntryMutation();

  // The week's entries are refetched after every approval, so the box has to
  // follow the record rather than hold a draft against a stale one.
  useEffect(() => {
    setDraft(existing);
  }, [existing, entry?.id]);

  // Whose move it is. `awaiting_supervisor` is the one that matters here: the
  // candidate has answered and this entry is now sitting on the supervisor,
  // who ends the review by approving it and in no other way.
  const state = String(entry?.review_state ?? "none");
  const awaitingCandidate = state === "awaiting_candidate";
  const awaitingSupervisor = state === "awaiting_supervisor";
  const dirty = draft.trim() !== existing.trim();

  const submit = async () => {
    setMsg(null);
    setErr(null);
    try {
      const res: any = await saveComment({
        entryId: String(entry.id),
        comment: draft.trim(),
      }).unwrap();
      setMsg(res?.message || "Review saved.");
      await onSaved?.();
    } catch (e: any) {
      setErr(e?.data?.message || e?.message || "Could not save the review.");
    }
  };

  return (
    <div
      className={`mt-2 rounded border p-3 ${
        awaitingSupervisor
          ? "border-red-200 bg-red-50/50"
          : awaitingCandidate
          ? "border-amber-200 bg-amber-50/50"
          : "bg-gray-50"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
          <MessageSquare className="h-3.5 w-3.5" />
          Review comment
        </p>

        {existing ? (
          awaitingSupervisor ? (
            <span className="rounded-full border border-red-200 bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
              Answered — approve to clear
            </span>
          ) : awaitingCandidate ? (
            <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
              Awaiting candidate
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
              <CheckCircle2 className="h-3 w-3" />
              Closed on approval
            </span>
          )
        ) : null}
      </div>

      <textarea
        rows={2}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={disabled || isLoading}
        maxLength={2000}
        placeholder="Guidance for the candidate on this day — a training code to use, detail to add, something to correct."
        className="mt-2 w-full resize-y rounded border px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-100"
      />

      {msg ? (
        <p className="mt-1.5 text-[11px] font-medium text-emerald-700">{msg}</p>
      ) : null}
      {err ? (
        <p className="mt-1.5 flex items-start gap-1 text-[11px] text-rose-600">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {err}
        </p>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] text-gray-500">
          {/* Said out loud: writing again after the candidate has answered
              raises it afresh, which is usually what the supervisor wants but
              never what they expect to have to guess at. */}
          {awaitingSupervisor
            ? "The candidate has answered. Approving the entry closes this."
            : existing
            ? "Saving again puts it back to the candidate."
            : "The candidate sees this on their own logbook page."}
        </p>

        <button
          type="button"
          onClick={submit}
          disabled={disabled || isLoading || !dirty}
          className="rounded bg-gray-900 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-gray-800 disabled:opacity-40"
        >
          {isLoading ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving…
            </span>
          ) : existing && !draft.trim() ? (
            "Remove review"
          ) : (
            "Save review"
          )}
        </button>
      </div>
    </div>
  );
}
